import React from "react";
import { Button } from "../ui/button";
import { AlertTriangle, Video as VideoIcon, Headphones, ArrowRight } from "lucide-react";

// Iter-40 — Publish confirmation modal.
// Renders ONLY when the candidate has an existing published intro for
// the same mode (so this would replace it). For brand-new candidates
// publishing their first intro, the parent skips this modal.
//
// Props:
//   open: boolean
//   mode: "video" | "audio"
//   currentPublished: { duration_seconds, published_at } | null
//   newDraft: { duration_seconds } | null
//   onConfirm: () => void
//   onCancel: () => void
//   publishing: boolean

const fmtDuration = (s) => {
  if (s === null || s === undefined) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch (_) { return "—"; }
};

const SideBySideCard = ({ title, mode, durationSeconds, dateLabel, tone, testId }) => {
  const Icon = mode === "audio" ? Headphones : VideoIcon;
  const toneClasses = tone === "current"
    ? "border-slate-200 bg-slate-50"
    : "border-blue-200 bg-blue-50";
  const labelTone = tone === "current" ? "text-slate-500" : "text-blue-700";
  return (
    <div className={`rounded-xl border ${toneClasses} p-4`} data-testid={testId}>
      <p className={`text-[10px] uppercase tracking-wide font-bold ${labelTone}`}>{title}</p>
      <div className="flex items-center gap-2 mt-1.5">
        <Icon className={`h-4 w-4 ${tone === "current" ? "text-slate-400" : "text-blue-600"}`} />
        <span className="text-sm font-semibold capitalize text-slate-900">{mode}</span>
      </div>
      <div className="mt-3 space-y-1 text-xs">
        <div className="flex justify-between">
          <span className="text-slate-500">Duration</span>
          <span className="font-medium text-slate-900 tabular-nums">{fmtDuration(durationSeconds)}</span>
        </div>
        {dateLabel && (
          <div className="flex justify-between">
            <span className="text-slate-500">Date</span>
            <span className="font-medium text-slate-900">{dateLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const PublishConfirmModal = ({ open, mode, currentPublished, newDraft, onConfirm, onCancel, publishing }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="publish-confirm-modal"
      onClick={(e) => {
        // Click outside dismisses, but NOT while a publish call is in flight.
        if (e.target === e.currentTarget && !publishing) onCancel();
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900" data-testid="publish-confirm-title">
              Replace your live {mode} introduction?
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Publishing will retire your current live {mode} introduction and replace it with the new draft.
              Already-printed Application Package QRs keep pointing at the retired version, but
              <span className="font-semibold"> any new QR scans on your public profile will see the new intro</span>.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-3">
          <SideBySideCard
            title="Currently live"
            mode={mode}
            durationSeconds={currentPublished?.duration_seconds}
            dateLabel={fmtDate(currentPublished?.published_at)}
            tone="current"
            testId="publish-confirm-current"
          />
          <ArrowRight className="h-5 w-5 text-slate-400 mx-auto rotate-90 sm:rotate-0" />
          <SideBySideCard
            title="New draft"
            mode={mode}
            durationSeconds={newDraft?.duration_seconds}
            dateLabel="About to publish"
            tone="new"
            testId="publish-confirm-new"
          />
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={publishing}
            data-testid="publish-confirm-cancel-btn"
          >
            Keep current live intro
          </Button>
          <Button
            onClick={onConfirm}
            disabled={publishing}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="publish-confirm-publish-btn"
          >
            {publishing ? "Publishing…" : `Publish new ${mode} intro`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PublishConfirmModal;
